import { FC, useCallback, useEffect, useMemo, useState } from "react";
import DelegationAddWorker from "../../types/delegation-add-worker";
import delegationTranslationsHelper from "../../../../../../languages/delegation-translations.helper";
import DelegationAddWorkerSelectOption from "../../types/delegation-add-worker-select-option";
import useCargoCompanyPassengerList from "../../../../../../common/services/cargo-company/passenger/list/use-cargo-company-passenger-list";
import useAbort from "../../../../../../common/hooks/use-abort";
import useCargoCompanyDispatcherList from "../../../../../../common/services/cargo-company/dispatcher/list/use-cargo-company-dispatcher-list";
import { debounce } from "lodash";
import CargoCompanyPassengerListParams from "../../../../../../common/services/cargo-company/passenger/list/cargo-company-passenger-list-params";
import CargoCompanyDispatcherListParams from "../../../../../../common/services/cargo-company/dispatcher/list/cargo-company-dispatcher-list-params";
import { DelegationAddWorkerListItem } from "../../types/delegation-add-worker-list";
import SingleSelectComponent from "../../../../../../common/components/form/select/single-select/single-select.component";
import delegationAddWorkerListFactory from "../../factory/delegation-add-worker-list.factory";
import delegationAddWorkerFactory from "./delegation-add-worker.factory";

type DelegationAddWorkerSelectProps = {
  worker: DelegationAddWorker | null;
  onWorkerChange: (worker: DelegationAddWorker | null) => void;
  workerCompanyUuid: string | null;
  onBlur: () => void;
  idForTesting?: string;
  hasError?: boolean;
};

const DelegationAddWorkerSelectComponent: FC<DelegationAddWorkerSelectProps> = (
  props,
) => {
  const translations =
    delegationTranslationsHelper.getDelegationAddTranslations().workers;

  const [workerSearchQuery, setWorkerSearchQuery] = useState("");
  const [workerSelectOptions, setWorkersSelectOptions] = useState<
    DelegationAddWorkerSelectOption[]
  >([]);

  const passengerList = useCargoCompanyPassengerList();
  const passengerListAbort = useAbort();

  const dispatcherList = useCargoCompanyDispatcherList();
  const dispatcherListAbort = useAbort();

  const loadPassengerListDebounced = useCallback(
    debounce((workerCoId: string, searchQuery: string) => {
      const loadParams: CargoCompanyPassengerListParams = {
        companyUuid: workerCoId,
        searchQuery: searchQuery,
      };

      return passengerList.load(loadParams, passengerListAbort.signal);
    }, 500),
    [],
  );

  useEffect(() => {
    if (!props.workerCompanyUuid) {
      return;
    }

    loadPassengerListDebounced(props.workerCompanyUuid, workerSearchQuery);

    return () => {
      passengerListAbort.revoke();
      loadPassengerListDebounced.cancel();
    };
  }, [props.workerCompanyUuid, workerSearchQuery, loadPassengerListDebounced]);

  useEffect(() => {
    if (!props.workerCompanyUuid) {
      return;
    }

    const loadParams: CargoCompanyDispatcherListParams = {
      companyUuid: props.workerCompanyUuid,
    };

    dispatcherList.load(loadParams, dispatcherListAbort.signal);

    return () => {
      dispatcherListAbort.revoke();
    };
  }, [props.workerCompanyUuid]);

  useEffect(() => {
    const passengerWorkerList =
      delegationAddWorkerListFactory.createPassengerList(passengerList.data);
    const dispatcherWorkerList =
      delegationAddWorkerListFactory.createDispatcherList(
        dispatcherList.data.filter((worker) =>
          worker.displayName
            .toLowerCase()
            .includes(workerSearchQuery.toLowerCase()),
        ),
      );

    const workers: DelegationAddWorkerListItem[] =
      delegationAddWorkerListFactory.combineWorkerLists(
        passengerWorkerList,
        dispatcherWorkerList,
      );

    const options: DelegationAddWorkerSelectOption[] =
      delegationAddWorkerFactory.createWorkerSelectOptions(workers);

    setWorkersSelectOptions(options);
  }, [passengerList.data, dispatcherList.data]);

  const selectedWorkerSelectOption = useMemo(() => {
    if (!props.worker) {
      return null;
    }

    return delegationAddWorkerFactory.createWorkerSelectOption(props.worker);
  }, [props.worker]);

  const isWorkerListLoading = useMemo(() => {
    if (!props.workerCompanyUuid) {
      return false;
    }
    return passengerList.isLoading || dispatcherList.isLoading;
  }, [passengerList.isLoading, dispatcherList.isLoading]);

  const isWorkersFetchingError = useMemo(() => {
    return passengerList.isError || dispatcherList.isError;
  }, [passengerList.isError, dispatcherList.isError]);

  const isSearchSelectDisabled =
    isWorkersFetchingError || !props.workerCompanyUuid;

  return (
    <>
      <SingleSelectComponent
        classNames={{ root: "delegation_add_worker_list_item__select" }}
        placeholder={translations.selectPlaceholder}
        value={selectedWorkerSelectOption}
        onChange={(value: DelegationAddWorkerSelectOption | null) => {
          if (!value) {
            return;
          }

          props.onWorkerChange(value.value);
        }}
        isLoading={isWorkerListLoading}
        isDisabled={isSearchSelectDisabled}
        options={workerSelectOptions}
        isSearchable
        filterFunction={() => true}
        inputValue={workerSearchQuery}
        onInputChange={setWorkerSearchQuery}
        noOptionsMessage={(inputValue) => {
          if (inputValue) {
            return translations.searchNoOptionsMessage;
          }
          return translations.searchTipMessage;
        }}
        onBlur={props.onBlur}
        idForTesting={props.idForTesting}
        hasError={props.hasError}
      />
    </>
  );
};

export default DelegationAddWorkerSelectComponent;
